import React, {useCallback, useEffect, useState} from 'react';
import {
    Box,
    Button,
    FormControl,
    FormControlLabel,
    FormLabel,
    MenuItem,
    Radio,
    RadioGroup,
    TextField
} from '@mui/material';
import PropTypes from 'prop-types';
import Typography from '@mui/material/Typography';
import RequiredCheckbox from './Shared/RequiredCheckbox';
import RichEditor from './Shared/RichEditor';
import RichEditorResultText from './Shared/RichEditorResultText';

const LinearScale = ({
                         index,
                         componentId,
                         value,
                         onChange,
                         descriptionHtml,
                         onDescriptionHtmlChange,
                         commitDescriptionHtml,
                         isEditMode,
                         enableValidation,
                         startFrom,
                         endAt,
                         startLabel,
                         endLabel,
                         isFormArchived,
                         isSubmitted,
                         readOnly,
                         placeholderValues,
                         accessLevel,
                         audience,
                         recipientType,
                         linkedToStudents,
                         submitMode
                     }) => {
    const startPositionOptions = [0, 1];
    const endPositionOptions = [2, 3, 4, 5, 6, 7, 8, 9, 10];

    const [draftDescriptionHtml, setDraftDescriptionHtml] = useState(descriptionHtml || '');
    const [isEditorFocused, setIsEditorFocused] = useState(false);

    useEffect(() => {
        if (isEditorFocused) return;
        setDraftDescriptionHtml(descriptionHtml || '');
    }, [descriptionHtml, isEditorFocused]);

    const handleDraftChange = useCallback((nextHtml) => {
        setDraftDescriptionHtml(nextHtml);
        onDescriptionHtmlChange(nextHtml);
    }, [onDescriptionHtmlChange]);

    const handleBlur = useCallback(() => {
        setIsEditorFocused(false);
        commitDescriptionHtml();
    }, [commitDescriptionHtml]);

    const handleFocus = useCallback(() => {
        setIsEditorFocused(true);
    }, []);

    const generateRadioButtons = () => {
        const radios = [];
        for (let i = startFrom; i <= endAt; i++) {
            radios.push(
                <FormControlLabel
                    key={i}
                    value={i.toString()}
                    control={<Radio/>}
                    label={i.toString()}
                    labelPlacement="top"
                    sx={{width: '10px'}}
                />
            );
        }
        return radios;
    };

    return (
        <Box sx={{mt: 2}}>
            {isEditMode
                ? (
                    <>
                        <RichEditor
                            currentDescription={draftDescriptionHtml}
                            setCurrentDescription={handleDraftChange}
                            isFormLive={isFormArchived}
                            accessLevel={accessLevel}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            onFocus={handleFocus}
                            onBlur={handleBlur}
                        />
                        <Box display="flex" gap={2} alignItems="center">
                        <TextField
                            select
                            value={startFrom}
                            variant="standard"
                            disabled={isFormArchived}
                            onChange={e => onChange({index, startFrom: e.target.value})}
                        >
                            {startPositionOptions.map(option => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </TextField>
                        <Typography>to</Typography>
                        <TextField
                            select
                            variant="standard"
                            value={endAt}
                            disabled={isFormArchived}
                            onChange={e => onChange({index, endAt: e.target.value})}
                        >
                            {endPositionOptions.map(option => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>
                        <Box display="flex" gap={2} alignItems="center" py={2}>
                        <TextField
                            label="Start label (optional)"
                            value={startLabel}
                            disabled={isFormArchived}
                            onChange={e => onChange({index, startLabel: e.target.value})}
                        />
                        <TextField
                            label="End label (optional)"
                            value={endLabel}
                            disabled={isFormArchived}
                            onChange={e => onChange({index, endLabel: e.target.value})}
                        />
                    </Box>
                        <RequiredCheckbox
                            enableValidation={enableValidation}
                            index={index}
                            onChange={onChange}
                            isFormArchived={isFormArchived}
                        />
                </>
                )
                : (
                    <>
                        <RichEditorResultText
                            id={componentId}
                            description={descriptionHtml}
                            placeholderValues={placeholderValues}
                            readOnly={readOnly}
                            enableValidation={enableValidation}
                            audience={audience}
                            recipientType={recipientType}
                            linkedToStudents={linkedToStudents}
                            submitMode={submitMode}
                        />
                    <FormControl component="fieldset" sx={{width: '100%'}}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" sx={{width: '100%'}}>
                            <FormLabel component="legend">{startLabel}</FormLabel>
                            <RadioGroup
                                key={value}
                                row
                                aria-label="number"
                                sx={{flex: 1, justifyContent: 'space-around'}}
                                value={value}
                                onChange={e => onChange({
                                    id: componentId,
                                    value: e.target.value
                                })}
                            >
                                {generateRadioButtons()}
                            </RadioGroup>
                            <FormLabel component="legend">{endLabel}</FormLabel>
                        </Box>
                        {value && !isSubmitted && (
                            <Button
                                onClick={() => onChange({
                                    id: componentId,
                                    value: null
                                })}
                                color="secondary"
                            >
                                Clear Selection
                            </Button>
                        )}
                    </FormControl>
                </>
                )}
        </Box>
    );
};

LinearScale.propTypes = {
    componentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    value: PropTypes.string,
    index: PropTypes.number,
    descriptionHtml: PropTypes.string,
    onDescriptionHtmlChange: PropTypes.func.isRequired,
    commitDescriptionHtml: PropTypes.func.isRequired,
    enableValidation: PropTypes.bool,
    onChange: PropTypes.func,
    isEditMode: PropTypes.bool,
    isFormArchived: PropTypes.bool,
    startFrom: PropTypes.number,
    endAt: PropTypes.number,
    startLabel: PropTypes.string,
    endLabel: PropTypes.string,
    isSubmitted: PropTypes.bool,
    readOnly: PropTypes.bool,
    placeholderValues: PropTypes.object,
    accessLevel: PropTypes.number,
    audience: PropTypes.number,
    recipientType: PropTypes.string,
    linkedToStudents: PropTypes.bool,
    submitMode: PropTypes.bool
};

export default LinearScale;